/**
 * The colours every chart draws with.
 *
 * One rule keeps the screen on brand: routine series are shades of the accent
 * (`--primary`, which follows the person's accent preference), and colour is
 * spent only on the exception — a failure, a deletion, a closure, something to
 * review. Categories without a meaning (chantiers, channels) take accent shades
 * by rank, never a rainbow.
 *
 * A chart never names a raw colour: it picks a tone, and the tone resolves to a
 * theme token, so light and dark need nothing per chart. Tints and hatching mix
 * the tone into the card surface rather than lowering opacity, so a pale
 * segment stays opaque over a track or a grid line.
 */

export type ChartTone =
  | "success"
  | "warning"
  | "destructive"
  | "info"
  | "expiring"
  | "primary"
  | "muted";

export const TONE_COLOR = {
  success: "var(--success)",
  warning: "var(--warning)",
  destructive: "var(--destructive)",
  info: "var(--info)",
  expiring: "var(--expiring)",
  primary: "var(--primary)",
  muted: "color-mix(in oklab, var(--muted-foreground) 45%, var(--card))",
} as const satisfies Record<ChartTone, string>;

/**
 * Accent shades for categories without a meaning, strongest first. Four stay
 * distinguishable side by side; beyond that a legend and the card carry the name.
 */
export const ACCENT_SHADES = [
  "var(--primary)",
  "color-mix(in oklab, var(--primary) 62%, var(--card))",
  "color-mix(in oklab, var(--primary) 38%, var(--card))",
  "color-mix(in oklab, var(--primary) 82%, var(--foreground))",
] as const;

/** The shade for the category at `rank`. */
export function accentShade(rank: number): string {
  return ACCENT_SHADES[rank % ACCENT_SHADES.length]!;
}

/** The quiet ground the exceptions stand out from: what is fine, in use, routine. */
export const ROUTINE_COLOR = "color-mix(in oklab, var(--primary) 32%, var(--card))";

/** The empty part of a track: a day with nothing, the seats nobody holds. */
export const TRACK_COLOR = "var(--muted)";

/**
 * A colour mixed into the card, as an opaque tint. oklab, since the card's
 * explicit 0 hue bends an oklch mix towards red.
 */
export function tint(color: string, percent: number): string {
  return `color-mix(in oklab, ${color} ${percent}%, var(--card))`;
}

/** Stripe pitch and stripe width of the diagonal hatch, in pixels. */
export const HATCH_PITCH = 6;
export const HATCH_STRIPE = 2;

/**
 * Diagonal hatching as a CSS background: the treatment for a part that stands
 * apart from the solid ones (files being deleted, a closed day, a failed run).
 * The stripes lean the same way as the audit log's SVG hatch.
 */
export function hatch(color: string, { ground = 26, stripe = 70 } = {}): string {
  return `repeating-linear-gradient(135deg, ${tint(color, stripe)} 0 ${HATCH_STRIPE}px, ${tint(
    color,
    ground,
  )} ${HATCH_STRIPE}px ${HATCH_PITCH}px)`;
}

/** The fill of one mark: solid by default, hatched when the part stands apart. */
export function markBackground(color: string, hatched = false): string {
  return hatched ? hatch(color) : color;
}
